/**
 * Attach existing Unique Users to Batch/Group Instance(s) (#31 / IC-WS-B6).
 *
 * Default: each checked ready row attaches to its **Suggested group** (Group Instance).
 * Optional: force all selected into one override Group Instance.
 * From Groups page: target is locked to that instance.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { fetchAllUsers } from '../../services/api';
import {
  addGroupMembers,
  listGroupInstances,
  previewAttachExistingUsers,
  AttachPreviewRow,
  AttachPreviewResponse,
} from '../../services/workspaceApi';

export type AttachSeedUser = {
  id?: number | string;
  userId: string;
  email?: string | null;
  programCode?: string | null;
};

const EMPTY_USERS: AttachSeedUser[] = [];

type Props = {
  open: boolean;
  onClose: () => void;
  users?: AttachSeedUser[];
  allowPickUsers?: boolean;
  lockedGroupInstanceId?: number;
  lockedGroupName?: string;
  onAttached?: (groupInstanceId: number, count: number) => void;
};

const statusColor = (status: AttachPreviewRow['status']): string => {
  switch (status) {
    case 'ready':
      return 'green';
    case 'already_member':
      return 'blue';
    case 'conflict':
      return 'orange';
    default:
      return 'default';
  }
};

/** Ready rows that have a Group Instance suggestion (or match forced target). */
function readyAttachKeys(rows: AttachPreviewRow[]): React.Key[] {
  return rows
    .filter((r) => r.status === 'ready' && r.suggestedGroupInstanceId != null)
    .map((r) => String(r.uniqueUserId));
}

const AttachExistingUsersModal: React.FC<Props> = ({
  open,
  onClose,
  users: usersProp,
  allowPickUsers = false,
  lockedGroupInstanceId,
  lockedGroupName,
  onAttached,
}) => {
  const seedUsers = usersProp && usersProp.length > 0 ? usersProp : EMPTY_USERS;
  const locked = lockedGroupInstanceId != null;

  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [preview, setPreview] = useState<AttachPreviewResponse | null>(null);
  /** Optional force-all-into-one Group Instance (null = use each row’s Suggested group). */
  const [forceTargetId, setForceTargetId] = useState<number | undefined>(lockedGroupInstanceId);
  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const [catalog, setCatalog] = useState<AttachSeedUser[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [rowFilter, setRowFilter] = useState('');
  const [excludeFilter, setExcludeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');
  const [suggestedGroupFilter, setSuggestedGroupFilter] = useState<number | 'all'>('all');
  const [groupOptionsFallback, setGroupOptionsFallback] = useState<
    { value: number; label: string }[]
  >([]);
  const previewReqId = useRef(0);

  const seedKey = useMemo(
    () => seedUsers.map((u) => `${u.id ?? ''}:${u.userId}`).join('|'),
    [seedUsers],
  );

  const activeUsers: AttachSeedUser[] = useMemo(() => {
    if (seedUsers.length > 0) return seedUsers;
    if (!allowPickUsers) return EMPTY_USERS;
    return catalog.filter((u) => {
      const id = typeof u.id === 'number' ? u.id : parseInt(String(u.id), 10);
      return pickedIds.includes(id);
    });
  }, [seedKey, allowPickUsers, catalog, pickedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const uniqueUserIds = useMemo(
    () =>
      activeUsers
        .map((u) => (typeof u.id === 'number' ? u.id : parseInt(String(u.id), 10)))
        .filter((n) => Number.isFinite(n) && n > 0),
    [activeUsers],
  );
  const userIdsKey = useMemo(
    () => activeUsers.map((u) => u.userId).filter(Boolean).join(','),
    [activeUsers],
  );
  const uniqueIdsKey = useMemo(() => uniqueUserIds.join(','), [uniqueUserIds]);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setForceTargetId(lockedGroupInstanceId);
      setPickedIds([]);
      setSelectedRowKeys([]);
      setRowFilter('');
      setExcludeFilter('');
      setStatusFilter('all');
      setSuggestedGroupFilter('all');
      setLoading(false);
      return;
    }
    setForceTargetId(lockedGroupInstanceId);
    setSelectedRowKeys([]);
    if (allowPickUsers && seedUsers.length === 0) {
      setCatalogLoading(true);
      fetchAllUsers()
        .then((rows) => {
          setCatalog(
            rows.map((u) => ({
              id: typeof u.id === 'string' ? parseInt(u.id, 10) : u.id,
              userId: u.userId,
              email: u.email,
              programCode: u.programCode,
            })),
          );
        })
        .catch(() => message.error('Failed to load Unique Users'))
        .finally(() => setCatalogLoading(false));
    }
    if (!locked) {
      listGroupInstances()
        .then((rows) => {
          setGroupOptionsFallback(
            (Array.isArray(rows) ? rows : []).map((g) => ({
              value: g.id,
              label: `${g.name || `#${g.id}`}${g.groupType ? ` (${g.groupType.code}/${g.groupType.kind})` : ''}`,
            })),
          );
        })
        .catch(() => setGroupOptionsFallback([]));
    }
  }, [open, lockedGroupInstanceId, allowPickUsers, seedUsers.length, locked]);

  const loadPreview = useCallback(async () => {
    if (!open || (!uniqueIdsKey && !userIdsKey)) {
      setPreview(null);
      setSelectedRowKeys([]);
      setLoading(false);
      return;
    }
    const reqId = ++previewReqId.current;
    setLoading(true);
    try {
      const ids = uniqueIdsKey
        ? uniqueIdsKey.split(',').map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n))
        : [];
      const uids = userIdsKey ? userIdsKey.split(',').filter(Boolean) : [];
      // Only pass groupInstanceId when forcing one Group Instance (locked page or override).
      const forceId = locked ? lockedGroupInstanceId : forceTargetId;
      const data = await previewAttachExistingUsers({
        ...(ids.length > 0 ? { uniqueUserIds: ids } : { userIds: uids }),
        ...(forceId != null ? { groupInstanceId: forceId } : {}),
      });
      if (reqId !== previewReqId.current) return;
      setPreview(data);
      setSelectedRowKeys(readyAttachKeys(data.rows));
    } catch (e) {
      if (reqId !== previewReqId.current) return;
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to preview attach');
      setPreview(null);
      setSelectedRowKeys([]);
    } finally {
      if (reqId === previewReqId.current) setLoading(false);
    }
  }, [open, uniqueIdsKey, userIdsKey, forceTargetId, locked, lockedGroupInstanceId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const groupOptions = useMemo(() => {
    const fromPreview = (preview?.groups || []).map((g) => ({
      value: g.id,
      label: `${g.name}${g.groupTypeCode ? ` (${g.groupTypeCode}/${g.groupTypeKind || '—'})` : ''}`,
    }));
    return fromPreview.length > 0 ? fromPreview : groupOptionsFallback;
  }, [preview?.groups, groupOptionsFallback]);

  const catalogOptions = useMemo(
    () =>
      catalog.map((u) => {
        const id = typeof u.id === 'number' ? u.id : parseInt(String(u.id), 10);
        return {
          value: id,
          label: `${u.userId}${u.email ? ` — ${u.email}` : ''}${u.programCode ? ` [${u.programCode}]` : ''}`,
        };
      }),
    [catalog],
  );

  const suggestedGroupOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of preview?.rows || []) {
      if (r.suggestedGroupInstanceId != null) {
        map.set(r.suggestedGroupInstanceId, r.suggestedGroupName || `#${r.suggestedGroupInstanceId}`);
      }
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [preview?.rows]);

  const filteredRows = useMemo(() => {
    let rows = preview?.rows || [];
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    if (suggestedGroupFilter !== 'all') {
      rows = rows.filter((r) => r.suggestedGroupInstanceId === suggestedGroupFilter);
    }
    const include = rowFilter.trim().toLowerCase();
    if (include) {
      rows = rows.filter(
        (r) =>
          r.userId.toLowerCase().includes(include) ||
          (r.email || '').toLowerCase().includes(include) ||
          (r.programCode || '').toLowerCase().includes(include) ||
          (r.programInstanceId || '').toLowerCase().includes(include) ||
          (r.suggestedGroupName || '').toLowerCase().includes(include),
      );
    }
    const exclude = excludeFilter.trim().toLowerCase();
    if (exclude) {
      rows = rows.filter(
        (r) =>
          !(
            r.userId.toLowerCase().includes(exclude) ||
            (r.email || '').toLowerCase().includes(exclude) ||
            (r.programInstanceId || '').toLowerCase().includes(exclude) ||
            (r.suggestedGroupName || '').toLowerCase().includes(exclude)
          ),
      );
    }
    return rows;
  }, [preview?.rows, rowFilter, excludeFilter, statusFilter, suggestedGroupFilter]);

  const selectedReadyRows = useMemo(() => {
    if (!preview) return [] as AttachPreviewRow[];
    const selected = new Set(selectedRowKeys.map(String));
    return preview.rows.filter(
      (r) =>
        selected.has(String(r.uniqueUserId)) &&
        r.status === 'ready' &&
        r.suggestedGroupInstanceId != null,
    );
  }, [preview, selectedRowKeys]);

  const attachableSelectedCount = selectedReadyRows.length;

  const attachPlanByGroup = useMemo(() => {
    const map = new Map<number, { name: string; count: number }>();
    for (const r of selectedReadyRows) {
      const gid = r.suggestedGroupInstanceId!;
      const cur = map.get(gid) || { name: r.suggestedGroupName || `#${gid}`, count: 0 };
      cur.count += 1;
      map.set(gid, cur);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  }, [selectedReadyRows]);

  /** Replace selection with visible ready only. */
  const selectReadyVisible = () => setSelectedRowKeys(readyAttachKeys(filteredRows));

  /** Keep existing selection; add visible ready. */
  const addReadyVisibleToSelection = () => {
    const add = readyAttachKeys(filteredRows).map(String);
    setSelectedRowKeys((prev) => Array.from(new Set([...prev.map(String), ...add])));
  };

  /** Drop only currently visible rows from selection (keep others). */
  const clearVisibleSelection = () => {
    const visible = new Set(filteredRows.map((r) => String(r.uniqueUserId)));
    setSelectedRowKeys((prev) => prev.filter((k) => !visible.has(String(k))));
  };

  /** Invert ready-checkable rows among visible; leave hidden selection alone. */
  const invertVisibleSelection = () => {
    const visibleReady = readyAttachKeys(filteredRows).map(String);
    const prev = new Set(selectedRowKeys.map(String));
    for (const id of visibleReady) {
      if (prev.has(id)) prev.delete(id);
      else prev.add(id);
    }
    setSelectedRowKeys(Array.from(prev));
  };

  const clearAllSelection = () => setSelectedRowKeys([]);

  const clearFilters = () => {
    setRowFilter('');
    setExcludeFilter('');
    setStatusFilter('all');
    setSuggestedGroupFilter('all');
  };
  const handleOk = async () => {
    if (selectedReadyRows.length === 0) {
      message.info('Select ready rows that have a Suggested group (Group Instance)');
      return;
    }

    const missing = selectedReadyRows.filter((r) => r.suggestedGroupInstanceId == null);
    if (missing.length > 0) {
      message.warning('Some selected rows have no Suggested group — uncheck them or set Force into one group');
      return;
    }

    // Bucket by Group Instance id
    const buckets = new Map<number, typeof selectedReadyRows>();
    for (const r of selectedReadyRows) {
      const gid = r.suggestedGroupInstanceId!;
      const list = buckets.get(gid) || [];
      list.push(r);
      buckets.set(gid, list);
    }

    setAttaching(true);
    try {
      let total = 0;
      for (const [gid, rows] of Array.from(buckets.entries())) {
        await addGroupMembers(
          gid,
          rows.map((r: AttachPreviewRow) => ({
            userId: r.userId,
            uniqueUserId: r.uniqueUserId,
            email: r.email || undefined,
            isPrimary: false,
          })),
        );
        total += rows.length;
      }
      const plan =
        attachPlanByGroup.length === 1
          ? attachPlanByGroup[0].name
          : `${attachPlanByGroup.length} Group Instances`;
      message.success(`Attached ${total} user(s) → ${plan}`);
      onAttached?.(buckets.keys().next().value as number, total);
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Attach failed');
    } finally {
      setAttaching(false);
    }
  };

  const columns = [
    { title: 'User ID', dataIndex: 'userId', key: 'userId', width: 140 },
    { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true },
    {
      title: 'Program',
      key: 'program',
      width: 90,
      render: (_: unknown, r: AttachPreviewRow) => r.programCode || '—',
    },
    {
      title: 'Suggested group (instance)',
      key: 'group',
      ellipsis: true,
      render: (_: unknown, r: AttachPreviewRow) => r.suggestedGroupName || '—',
    },
    {
      title: 'Match',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 80,
      render: (c: string) => <Tag>{c}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: AttachPreviewRow['status']) => (
        <Tag color={statusColor(s)}>{s.replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: 'Notes',
      key: 'notes',
      ellipsis: true,
      render: (_: unknown, r: AttachPreviewRow) =>
        [...r.conflicts, ...r.reasons.slice(0, 1)].join('; ') || '—',
    },
  ];

  const title = locked
    ? `Add existing Unique Users → ${lockedGroupName || `#${lockedGroupInstanceId}`}`
    : 'Attach existing Unique Users to Group Instance(s)';

  const planSummary =
    attachPlanByGroup.length === 0
      ? null
      : attachPlanByGroup.map((p) => `${p.count} → ${p.name}`).join(' · ');

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={`Attach${attachableSelectedCount ? ` (${attachableSelectedCount})` : ''}`}
      confirmLoading={attaching}
      okButtonProps={{
        disabled: loading || attachableSelectedCount === 0,
      }}
      width={920}
      centered
      destroyOnClose
      zIndex={1100}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <strong>Overall flow:</strong> (1) Filter the list to a cohort (e.g. include{' '}
          <code>b23o</code>, exclude <code>b23n</code>, or pick Suggested group). (2) Select /
          deselect <em>visible</em> ready rows. (3) Change filter and add another cohort if needed.
          (4) Attach — each checked row goes to its <strong>Suggested group (Group Instance)</strong>
          unless you force one instance.
        </Typography.Paragraph>

        {allowPickUsers && seedUsers.length === 0 && (
          <div>
            <Typography.Text strong>Unique Users</Typography.Text>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Search and select existing people"
              style={{ width: '100%', marginTop: 4 }}
              loading={catalogLoading}
              options={catalogOptions}
              value={pickedIds}
              onChange={(v) => setPickedIds(v)}
              maxTagCount="responsive"
            />
          </div>
        )}

        {locked ? (
          <Alert
            type="info"
            showIcon
            message={`Target locked: ${lockedGroupName || `#${lockedGroupInstanceId}`} (all attaches go here)`}
          />
        ) : (
          <div>
            <Typography.Text strong>Force into one Group Instance (optional)</Typography.Text>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Leave empty = use each row’s Suggested group"
              style={{ width: '100%', marginTop: 4 }}
              value={forceTargetId}
              options={groupOptions}
              onChange={(v) => setForceTargetId(v)}
              loading={loading && !preview}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              Empty = multi-instance attach from the Suggested group column. Set only when you
              intentionally dump everyone into one batch.
            </Typography.Text>
          </div>
        )}

        {preview?.summary && (
          <Alert
            type="info"
            showIcon
            message={`${preview.summary.ready} ready · ${preview.summary.alreadyMember} already members · ${preview.summary.noSuggestion} no suggestion · ${preview.summary.conflict} conflicts`}
          />
        )}

        {planSummary && (
          <Alert type="success" showIcon message={`Attach plan: ${planSummary}`} />
        )}

        {preview && preview.rows.length > 0 && (
          <>
            <div>
              <Typography.Text strong>Filter (narrow what you see)</Typography.Text>
              <Space wrap style={{ width: '100%', marginTop: 8 }} size="small">
                <Input
                  allowClear
                  placeholder="Include: e.g. b23o"
                  value={rowFilter}
                  onChange={(e) => setRowFilter(e.target.value)}
                  style={{ width: 180 }}
                />
                <Input
                  allowClear
                  placeholder="Exclude: e.g. b23n"
                  value={excludeFilter}
                  onChange={(e) => setExcludeFilter(e.target.value)}
                  style={{ width: 180 }}
                />
                <Select
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v)}
                  style={{ width: 150 }}
                  options={[
                    { value: 'all', label: 'Status: all' },
                    { value: 'ready', label: 'Ready only' },
                    { value: 'already_member', label: 'Already member' },
                    { value: 'conflict', label: 'Conflict' },
                    { value: 'no_suggestion', label: 'No suggestion' },
                  ]}
                />
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Suggested group"
                  style={{ width: 200 }}
                  value={suggestedGroupFilter === 'all' ? undefined : suggestedGroupFilter}
                  options={suggestedGroupOptions}
                  onChange={(v) => setSuggestedGroupFilter(v == null ? 'all' : v)}
                />
                <Typography.Link onClick={clearFilters}>Clear filters</Typography.Link>
              </Space>
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Showing {filteredRows.length} of {preview.rows.length}
                {selectedRowKeys.length ? ` · ${selectedRowKeys.length} selected (any filter)` : ''}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text strong>Selection (acts on visible rows)</Typography.Text>
              <Space wrap style={{ display: 'block', marginTop: 6 }}>
                <Typography.Link onClick={selectReadyVisible}>
                  Select ready (visible) — replaces selection
                </Typography.Link>
                <Typography.Text type="secondary"> · </Typography.Text>
                <Typography.Link onClick={addReadyVisibleToSelection}>
                  Add visible ready
                </Typography.Link>
                <Typography.Text type="secondary"> · </Typography.Text>
                <Typography.Link onClick={clearVisibleSelection}>
                  Deselect visible
                </Typography.Link>
                <Typography.Text type="secondary"> · </Typography.Text>
                <Typography.Link onClick={invertVisibleSelection}>
                  Invert visible ready
                </Typography.Link>
                <Typography.Text type="secondary"> · </Typography.Text>
                <Typography.Link onClick={clearAllSelection}>Clear all selection</Typography.Link>
              </Space>
            </div>
          </>
        )}

        <Table
          className="attach-preview-table"
          size="small"
          rowKey={(r) => String(r.uniqueUserId)}
          loading={loading}
          pagination={false}
          scroll={{ y: 320 }}
          columns={columns as any}
          dataSource={filteredRows}
          locale={{
            emptyText: rowFilter.trim()
              ? 'No rows match this filter'
              : 'Select people to preview',
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            getCheckboxProps: (r: AttachPreviewRow) => ({
              disabled: r.status === 'already_member' || r.suggestedGroupInstanceId == null,
            }),
            preserveSelectedRowKeys: true,
          }}
        />
      </Space>
      <style>{`
        .attach-preview-table .ant-checkbox-inner {
          border: 2px solid #5A6474 !important;
          background: #fff !important;
          width: 16px !important;
          height: 16px !important;
        }
        .attach-preview-table .ant-checkbox-checked .ant-checkbox-inner {
          background: #0B5FD0 !important;
          border-color: #0B5FD0 !important;
        }
      `}</style>
    </Modal>
  );
};

export default AttachExistingUsersModal;
